import type { ReactNode } from "react";
import { useMemo } from "react";
import { PauseIcon, PencilIcon, TrashIcon } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import type { PopoverProps } from "tamagui";
import { Popover, Text, View, XStack } from "tamagui";

//TODO: make it better

export type Action =
  | "edit"
  | "deactivate"
  | "activate"
  | "delete"
  | "bloked-deletion";

const useGetActions = (args: {
  isActive: boolean;
  isNotRemovable: boolean;
}) => {
  const { t } = useTranslation("groups");

  const GROUP_ACTION = useMemo(
    () => [
      {
        name: t("group-action.rename"),
        action: "edit" as const,
        icon: PencilIcon,
        disabled: false,
        hidden: false,
      },
      {
        name: t("group-action.deactivate"),
        action: "deactivate" as const,
        icon: PauseIcon,
        disabled: false,
        hidden: false,
      },
      {
        name: t("group-action.activate"),
        action: "activate" as const,
        icon: PauseIcon,
        disabled: false,
        hidden: false,
      },
      {
        name: t("group-action.delete"),
        action: "delete" as const,
        icon: TrashIcon,
        disabled: false,
        hidden: false,
      },
      {
        name: t("group-action.bloked-deletion"),
        action: "bloked-deletion" as const,
        icon: TrashIcon,
        disabled: true,
        hidden: true,
      },
    ],
    [t],
  );

  return useMemo(() => {
    return GROUP_ACTION.map((action) => {
      if (action.action === "delete") {
        action.hidden = args.isNotRemovable;
      }

      if (action.action === "bloked-deletion") {
        action.hidden = !args.isNotRemovable;
      }

      if (action.action === "activate") {
        action.hidden = args.isActive;
      }

      if (action.action === "deactivate") {
        action.hidden = !args.isActive;
      }

      return {
        ...action,
      };
    }).filter((action) => !action.hidden);
  }, [GROUP_ACTION, args.isActive, args.isNotRemovable]);
};

export const GroupOptionsPopover: React.FC<
  Omit<PopoverProps, "children"> & {
    trigger: ReactNode;
    isGroupActive: boolean;
    isNotGroupRemovable: boolean;
    onActionPress?: (action: Action) => void;
  }
> = ({ trigger, isNotGroupRemovable, isGroupActive, onActionPress }) => {
  const actions = useGetActions({
    isActive: isGroupActive,
    isNotRemovable: isNotGroupRemovable,
  });

  return (
    <View
      data-id="group-options-popover"
      onPress={(event) => event.stopPropagation()}
    >
      <Popover placement="bottom-end" offset={1}>
        <Popover.Trigger data-id="group-options-popover-trigger">
          {trigger}
        </Popover.Trigger>
        <Popover.Content
          data-id="group-options-popover-content"
          borderColor={"$gray-600"}
          borderWidth={"$0.2"}
          padding={"$2"}
          borderRadius={"$5"}
          backgroundColor={"white"}
          alignItems={"flex-start"}
          gap={"$2"}
          maxWidth={280}
        >
          {actions.map((action, i) => {
            const Icon = action.icon;

            return (
              <Popover.Close
                data-id={`group-options-popover-action-${action.action}`}
                flexDirection="row"
                gap={"$0.5"}
                width="100%"
                key={action.action}
                alignItems="flex-start"
                justifyContent="flex-start"
                {...(actions.length - 1 !== i && {
                  height: "$4.1",
                  borderBottomWidth: "$0.2",
                  borderBottomColor: "$gray-100",
                })}
                cursor="pointer"
                disabledStyle={{
                  cursor: "not-allowed",
                }}
                disabled={action.disabled}
                onPress={() => onActionPress?.(action.action)}
              >
                <XStack gap={"$0.5"} alignItems="center" width="100%">
                  <View width={"$2.5"} height={"$2.5"}>
                    <Icon
                      {...(action.disabled && {
                        color: "#A1A7BA",
                      })}
                    />
                  </View>
                  <Text
                    fontFamily={"$body"}
                    fontWeight={"$4"}
                    fontSize={"$3"}
                    lineHeight={"$2"}
                    color={
                      action.action === "delete"
                        ? "$error-500"
                        : action.disabled
                          ? "$gray-secondary-400"
                          : "$gray-primary-900"
                    }
                  >
                    {action.name}
                  </Text>
                </XStack>
              </Popover.Close>
            );
          })}
        </Popover.Content>
      </Popover>
    </View>
  );
};
